<template>
  <section class="cohort-item-wrapper">
    <h2>
      {{ cohortName }}
      <VdIcon
        v-if="canEditTreatment && !cohort.isControl"
        store="far"
        name="trash"
        @click="emits('delete-cohort', index)"
      />
    </h2>
    <section>
      <InputText
        v-if="!cohort.isControl"
        id="cohortName"
        v-model="cohortName"
        :disabled="!canEditTreatment"
        :class="$style.textInput"
        placeholder="Cohort name"
      />
      <InputText
        v-if="!cohort.isControl"
        id="cohortDescription"
        v-model="cohortDescription"
        :class="$style.textInput"
        placeholder="Cohort description"
        :disabled="!canEditTreatment"
      />
    </section>
    <!-- lazy: only the active segment's panel renders — with thousands of linked offers, rendering every
         segment's read view at once was a large part of the page's render cost. -->
    <Tabs :value="0" lazy>
      <TabList :pt="{ tablist: { style: 'flex-wrap: wrap;' } }">
        <Tab v-for="(segment, i) in dynamicSegments" :key="segment.id" :value="i">{{ segment.name }}</Tab>
      </TabList>
      <TabPanels>
        <TabPanel v-for="(segment, i) in dynamicSegments" :key="segment.id" :value="i">
          <div :class="$style.formWrapper">
            <div v-if="!cohort.isControl" :class="$style.formField">
              <label for="selectedShop" :class="{ locked: !!getLayerLockingClass() }">
                Allocated shop
                <VdTooltip
                  v-if="!!getLayerLockingClass()"
                  :content="`The shop is already used by the layer '${getLayerLockingClass()}'`"
                  orientation="right"
                  alignment="end"
                >
                  <VdIcon store="fa" name="lock" class="locked" />
                </VdTooltip>
              </label>
              <Select
                id="selectedShop"
                :model-value="getShop(segment.id)"
                :options="shops"
                option-label="name"
                placeholder="Default shop"
                :style="{ minWidth: '240px', height: '32px' }"
                :disabled="!!getLayerLockingClass() || !canEditTreatment || cohort.isControl"
                :show-clear="true"
                @change="(e) => handleShopChange(e, segment.id)"
              >
                <template #value="slotProps">
                  <VdIcon v-if="getShop(segment.id)" store="far" name="shop" />
                  <span v-if="typeof slotProps.value === 'string'" :class="$style.label">
                    {{ cohort.isControl ? "Cannot add Shops to Control" : slotProps.value || "Select Shop" }}
                  </span>
                </template>
              </Select>
            </div>
            <div :class="$style.formField">
              <label for="selectedOffer">Offers</label>

              <!-- Multi-link (flag on): read view of the treatment's offers grouped by mode and campaign,
                   with an add-only picker. The offers' segments do NOT have to match this treatment's. -->
              <div v-if="flags.offerMultiLinkABTest.enabled" :class="$style.offersEditor">
                <!-- "Edit" opens edit mode: pills gain their remove button and the add picker shows below.
                     Every change is batched into ONE saveCohort request when "Save" is clicked; "Cancel"
                     discards it. Not gated on canEditTreatment: offers stay editable on published tests
                     (legacy MultiSelect parity — a published dynamic test takes link changes via
                     re-publish). -->
                <div :class="$style.pickerActions">
                  <button type="button" :class="$style.addOffersBtn" @click="togglePicker(segment.id)">
                    {{ pickerOpenSegmentId === segment.id ? "Save" : "Edit" }}
                  </button>
                  <button
                    v-if="pickerOpenSegmentId === segment.id"
                    type="button"
                    :class="$style.cancelEditBtn"
                    @click="cancelPicker(segment.id)"
                  >
                    Cancel
                  </button>
                  <VdIcon
                    v-if="savingOffersBySegment[segment.id]"
                    store="fas"
                    name="fa-spinner"
                    class="fa-spin"
                    custom-size="16px"
                    color="var(--vd-color-blue-default)"
                  />
                </div>

                <div v-if="pickerOpenSegmentId === segment.id" :class="$style.picker">
                  <div :class="$style.pickerTitle">Add offers</div>
                  <div :class="$style.pickerControls">
                    <InputText v-model="pickerSearch" placeholder="Search offers" :class="$style.pickerSearch" />
                    <Select
                      v-model="pickerSegmentFilter"
                      :options="openPickerSegmentOptions"
                      option-label="label"
                      option-value="value"
                      :class="$style.pickerFilter"
                    />
                  </div>
                  <div :class="$style.pickerList">
                    <template v-for="group in openPickerData.groups" :key="group.label">
                      <div
                        :class="[
                          $style.groupHeader,
                          $style.pickerGroupHeader,
                          group.mode === 'EXCLUDE' ? $style.modeExclude : $style.modeInclude,
                        ]"
                      >
                        {{ group.label }}
                      </div>
                      <div
                        v-for="offer in group.offers"
                        :key="offer.id"
                        :class="[$style.pickerOption, savingOffersBySegment[segment.id] ? $style.pickerDisabled : '']"
                        @click="addOffer(segment.id, offer.id)"
                      >
                        <VdIcon store="fa" name="plus" custom-size="12px" color="var(--vd-color-blue-default)" />
                        <span :class="$style.pickerOptionName">{{ offer.name }}</span>
                        <VdTooltip
                          v-if="offer.crossSegment"
                          orientation="top"
                          :content="`This offer uses different segments: ${offer.segmentNames}`"
                        >
                          <VdIcon
                            :class="$style.warningIcon"
                            store="fas"
                            name="triangle-exclamation"
                            custom-size="11px"
                          />
                        </VdTooltip>
                      </div>
                    </template>
                    <div v-if="!openPickerData.totalCount" :class="$style.pickerEmpty">
                      No offers match — they may all be linked already.
                    </div>
                  </div>
                  <div :class="$style.pickerCount">{{ openPickerData.countLine }}</div>
                </div>

                <!-- Read view of the linked offers; the add picker opens above it, next to the actions. -->
                <div :class="$style.editorBody">
                  <div :class="$style.readColumn">
                    <div v-if="!getSelectedOffers(segment.id).length" :class="$style.noOffers">
                      No offers linked to this treatment yet.
                    </div>
                    <div v-for="group in readViewGroups(segment.id)" :key="group.mode" :class="$style.readGroup">
                      <div
                        :class="[
                          $style.groupHeader,
                          $style.readGroupHeader,
                          group.mode === 'EXCLUDE' ? $style.modeExclude : $style.modeInclude,
                        ]"
                      >
                        <VdIcon
                          store="fas"
                          :name="group.mode === 'EXCLUDE' ? 'ban' : 'circle-check'"
                          custom-size="12px"
                          :color="group.mode === 'EXCLUDE' ? '#b91c1c' : '#15803d'"
                        />
                        {{ group.mode === "EXCLUDE" ? "Exclude" : "Include" }}
                        <span :class="$style.groupCount">{{ group.count }}</span>
                      </div>
                      <div v-for="campaign in group.campaigns" :key="campaign.id" :class="$style.campaignBlock">
                        <div :class="$style.campaignName">
                          {{ campaign.name }}
                          <span :class="$style.groupCount">({{ campaign.totalCount }})</span>
                        </div>
                        <div :class="$style.pillList">
                          <div v-for="offer in campaign.offers" :key="offer.id" :class="$style.pillRow">
                            <!-- LEFT of the chip so the ✕ column lines up regardless of chip width; always
                             rendered (visibility-hidden outside edit mode) so nothing shifts on Edit. -->
                            <a
                              :class="[
                                $style.pillRemove,
                                pickerOpenSegmentId !== segment.id ? $style.pillRemoveHidden : '',
                              ]"
                              aria-label="Remove offer"
                              @click="removeOffer(segment.id, offer.id)"
                            >
                              <VdIcon store="fa" name="xmark" custom-size="12px" />
                            </a>
                            <span :class="$style.offerPill">
                              {{ offer.name }}
                              <!-- Native title (not VdTooltip): one tooltip component per pill was a large part
                               of the read view's render cost at thousands of offers. -->
                              <span
                                v-if="offer.crossSegment"
                                :title="`This offer uses different segments: ${offer.segmentNames}`"
                              >
                                <VdIcon
                                  :class="$style.warningIcon"
                                  store="fas"
                                  name="triangle-exclamation"
                                  custom-size="11px"
                                />
                              </span>
                            </span>
                          </div>
                          <a
                            v-if="campaign.hiddenCount || campaign.expanded"
                            :class="$style.pillMoreLink"
                            @click="togglePillExpansion(campaign.key)"
                          >
                            {{ campaign.expanded ? "show less" : `+${campaign.hiddenCount} more` }}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Legacy (flag off): the original PENDING-offers MultiSelect, unchanged behavior. -->
              <div v-else :class="$style.offersRow">
                <MultiSelect
                  id="selectedOffer"
                  :model-value="getSelectedOffers(segment.id)"
                  :options="getOffersOptions(segment.id)"
                  option-label="name"
                  option-value="id"
                  :max-selected-labels="3"
                  placeholder="Select pending offers"
                  :style="{ minWidth: '240px', height: '32px' }"
                  :pt="{ header: { class: $style.selectAllHeader } }"
                  @change="(e) => handleOffersChange(e, segment.id)"
                  @hide="() => saveOffersForSegment(segment.id)"
                >
                  <template #option="slotProps">
                    <div :class="$style.multiselectOption">
                      <span>{{ slotProps.option.name }}</span>
                      <VdTooltip
                        v-if="
                          slotProps.selected &&
                          abTestState === AbtestState.Published &&
                          slotProps.option.status === OfferStatus.Published
                        "
                        orientation="top"
                        content="Unselecting this offer will remove it from the cohort treatment<br />and set it to 'Terminating' status"
                      >
                        <VdIcon :class="$style.warningIcon" store="fas" name="fa-triangle-exclamation" />
                      </VdTooltip>
                    </div>
                  </template>
                </MultiSelect>
                <VdIcon
                  v-if="savingOffersBySegment[segment.id]"
                  store="fas"
                  name="fa-spinner"
                  class="fa-spin"
                  custom-size="16px"
                  color="var(--vd-color-blue-default)"
                />
              </div>
            </div>
            <div v-if="!cohort.isControl" :class="$style.classesField">
              <label>Classes</label>
              <p v-if="getClasses(segment.id).length === 0">
                {{ cohort.isControl ? "Cannot add Classes to Control" : "No classes selected" }}
              </p>
              <section v-else>
                <div v-for="classItem in getClasses(segment.id)" :key="classItem.id" :class="$style.classItem">
                  <div>
                    <span><VdIcon store="fa" name="file-code" :class="$style.classIcon" /></span>
                    <span :class="$style.className">{{ classItem.name }}</span>
                  </div>
                  <VdIcon
                    v-if="canEditTreatment"
                    store="fa"
                    name="pen-to-square"
                    :class="$style.editIcon"
                    @click="() => onClassClick(segment.id, classItem.id)"
                  />
                </div>
              </section>
              <section v-if="canEditTreatment && !cohort.isControl" :class="$style.classesAddButton">
                <div class="classes-button-group">
                  <vd-button type="secondary" skin="blue" icon-left="fa:add" @click="() => onClassClick(segment.id)">
                    Add class
                  </vd-button>
                  <vd-button
                    v-if="getClasses(segment.id).length > 0 && dynamicSegments.length > 1"
                    type="secondary"
                    skin="blue"
                    icon-left="fa:copy"
                    @click="() => onClassCopy(segment.id)"
                  >
                    Copy classes
                  </vd-button>
                </div>
              </section>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </section>
</template>

<script setup lang="ts">
import {
  AbtestState,
  AbtestType,
  CohortFragment,
  CohortInput,
  CohortTreatment,
  GetCohortOfferOptionsQuery,
  OfferStatus,
  queryNames,
  useCohortFormPageQuery,
  useCohortFormPageSaveMutation,
  useGetShopsQuery,
} from "@/graphql";
import { getVTShopManagementClass } from "@/utils/getVTShopManagementClass";
import { mapCohortUpdatePayload } from "@/utils/mapCohortUpdatePayload";
import { useDebounceFn } from "@vueuse/core";
import InputText from "primevue/inputtext";
import MultiSelect, { MultiSelectChangeEvent } from "primevue/multiselect";
import Select, { SelectChangeEvent } from "primevue/select";
import Tab from "primevue/tab";
import TabList from "primevue/tablist";
import TabPanel from "primevue/tabpanel";
import TabPanels from "primevue/tabpanels";
import Tabs from "primevue/tabs";
import { PropType, computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useToast from "@/hooks/useToastNotification";

import VdIcon from "@/components/VdIcon/VdIcon.vue";
import useCampaigns from "@/modules/Offers/hooks/useCampaigns";
import { useSegments } from "@/modules/Offers/useSegments";
import links from "@/router/links";
import { VdTooltip } from "@voodoo.io/voodoo-ui";
import { cloneDeep } from "lodash";

const emits = defineEmits(["delete-cohort"]);

const props = defineProps({
  dynamicSegments: {
    type: Array as PropType<{ id: string; name: string }[]>,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
  abtestId: {
    type: String,
    required: true,
  },
  abTestState: {
    type: String,
    default: "DRAFT",
  },
  cohort: {
    type: Object as PropType<CohortFragment>,
    required: true,
  },
  routingCohortId: {
    type: String,
    default: "",
  },
  routingSegmentId: {
    type: String,
    default: "",
  },
  canEditTreatment: {
    type: Boolean,
    default: true,
  },
  // Slim offers list (GetCohortOfferOptions) fetched once by the page — see the `offers` computed below.
  offers: {
    type: Array as PropType<NonNullable<GetCohortOfferOptionsQuery["getOfferItems"]>>,
    default: () => [],
  },
  refetchOffers: {
    type: Function as PropType<() => Promise<unknown>>,
    required: true,
  },
});

const router = useRouter();
const { toastError, toastSuccess } = useToast();

// Multi-link (flag on): the picker shows already-linked offers and linking preserves each offer's mode.
// Flag off (legacy): only unlinked PENDING offers (plus this cohort's) are selectable, and linking forces
// the offer to INCLUDE (via useMultiLink=false on saveCohort).
const { flags } = useFeatureFlags(props.gameId);

const activeTab = ref(0);

const { result: cohortFormPageResult } = useCohortFormPageQuery({
  gameId: props.gameId,
  versionId: props.versionId,
  abtestId: props.abtestId,
});

const allLayers = computed(() => cohortFormPageResult.value?.game?.version?.layers ?? []);
const allClasses = computed(() => cohortFormPageResult.value?.game?.version?.classes ?? []);
const layerId = computed(() => cohortFormPageResult.value?.game?.version?.abtest?.layerId ?? undefined);
const shopManagementClassId = computed(
  () => allClasses.value.find(({ technicalName }) => technicalName === "VT.ShopManagementClass")?.instances[0]?.id
);
const getLayerLockingClass = () => {
  const shopClass = allClasses.value.find((item) => item.technicalName === "VT.ShopManagementClass");
  if (!shopClass) {
    return false;
  }
  const { lockedByLayer } = shopClass;
  return !!lockedByLayer && lockedByLayer !== layerId.value
    ? allLayers.value.find((l) => l.id === lockedByLayer)?.name
    : undefined;
};

const { result: shopsResult } = useGetShopsQuery(
  { appId: props.gameId, versionId: props.versionId },
  { fetchPolicy: "network-only" }
);

const shops = computed(() => shopsResult.value?.getShops ?? []);

const getShop = (segmentId: string) => {
  const treatment = props.cohort.treatments.find((t) => t.segmentId === segmentId);
  return treatment?.shopId ? shops.value.find((shop) => shop.id === treatment.shopId)?.name : "";
};

/**
 * Offers input. The list is fetched ONCE by DynamicTreatmentPage (slim GetCohortOfferOptions projection)
 * and passed down: one CohortItem renders per cohort, and per-cohort copies of the full offers query froze
 * the page for seconds on games with thousands of offers.
 */
const offers = computed(() => props.offers);
const refetchOffers = () => props.refetchOffers();

// Legacy (flag off) options: only PENDING offers not yet linked to a test are selectable, plus offers
// already attached to this cohort (so they can be removed). Already-linked offers are hidden, and the
// offer must target this treatment's segment.
const getOffersOptions = (segmentId: string) =>
  offers.value.filter(
    (offer) =>
      (offer?.conditions?.segments?.includes(segmentId) ?? false) &&
      ((offer?.status === "PENDING" && !offer?.abTestId) || offer?.cohortId === props.cohort.id)
  );

// Optimistic per-segment selection while the offers editor is open. Keeps the UI instant; the actual
// saveCohort mutation happens on dropdown close (legacy) or per add/remove action (multi-link).
const pendingOffersBySegment = ref<Record<string, string[]>>({});

// Whether a saveCohort is in flight for a segment (drives the loader beside the editor).
const savingOffersBySegment = ref<Record<string, boolean>>({});
const setSavingOffers = (segmentId: string, value: boolean) => {
  const next = { ...savingOffersBySegment.value };
  if (value) {
    next[segmentId] = true;
  } else {
    delete next[segmentId];
  }
  savingOffersBySegment.value = next;
};

const getSelectedOffers = (segmentId: string) => {
  const pending = pendingOffersBySegment.value[segmentId];
  if (pending !== undefined) {
    return pending;
  }
  const treatment = props.cohort.treatments.find((t) => t.segmentId === segmentId);
  return treatment?.offers ?? [];
};

const clearPendingOffers = (segmentId: string) => {
  const next = { ...pendingOffersBySegment.value };
  delete next[segmentId];
  pendingOffersBySegment.value = next;
};

const sameOfferIds = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id));

/**
 * Multi-link (flag on) read view + add-only picker. The treatment's offers render as removable pills
 * grouped by mode then campaign; the picker lists only unselected offers, searchable and filtered by a
 * segment dropdown that DEFAULTS to this treatment's segment (today's behavior) — "All segments" or any
 * other segment is the cross-segment path (an offer's segments no longer have to match the treatment's).
 */
const { segments: allSegments } = useSegments(props.gameId);
const segmentNameById = computed(() => new Map((allSegments.value ?? []).map((segment) => [segment.id, segment.name])));
const segmentName = (segmentId: string) => segmentNameById.value.get(segmentId) ?? segmentId;

// Campaign names come from useCampaigns' shared state; the fetch happens ONCE in DynamicTreatmentPage —
// one CohortItem renders per cohort, so fetching here would fire a /campaigns request per cohort.
const { campaigns } = useCampaigns(props.gameId, props.versionId);
const campaignNameById = computed(() => new Map(campaigns.value.map((c) => [c.id, c.name])));
const campaignNameOf = (offer: { campaignId?: string | null }) =>
  (offer.campaignId && campaignNameById.value.get(offer.campaignId)) || "No campaign";

const offersById = computed(() => new Map(offers.value.map((offer) => [offer?.id, offer])));

// Read view: the treatment's linked offers grouped by Include/Exclude, then by campaign. Cross-segment
// offers (segments not containing this treatment's segment) carry a marker — a legitimate state now.
// A campaign row shows at most this many pills until its "+N more" is expanded — a bulk-linked treatment
// can hold thousands of offers, and mounting one pill per offer was the read view's main render cost.
const PILL_CAP = 15;
const expandedPillGroups = ref(new Set<string>());
const togglePillExpansion = (key: string) => {
  const next = new Set(expandedPillGroups.value);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  expandedPillGroups.value = next;
};

const buildReadViewGroups = (segmentId: string) => {
  const selected = getSelectedOffers(segmentId)
    .map((offerId: string) => offersById.value.get(offerId))
    .filter((offer): offer is NonNullable<typeof offer> => !!offer);

  return (["INCLUDE", "EXCLUDE"] as const)
    .map((mode) => {
      const inMode = selected.filter((offer) =>
        mode === "EXCLUDE" ? offer.abtestLinkMode === "EXCLUDE" : offer.abtestLinkMode !== "EXCLUDE"
      );
      const byCampaign = new Map<string, { id: string; name: string; offers: typeof inMode }>();
      inMode.forEach((offer) => {
        const id = offer.campaignId || "noCampaign";
        if (!byCampaign.has(id)) {
          byCampaign.set(id, { id, name: campaignNameOf(offer), offers: [] });
        }
        byCampaign.get(id)!.offers.push(offer);
      });
      return {
        mode,
        count: inMode.length,
        campaigns: [...byCampaign.values()]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((campaign) => {
            const key = `${segmentId}:${mode}:${campaign.id}`;
            const expanded = expandedPillGroups.value.has(key);
            const shown = expanded ? campaign.offers : campaign.offers.slice(0, PILL_CAP);
            return {
              id: campaign.id,
              name: campaign.name,
              key,
              expanded,
              totalCount: campaign.offers.length,
              hiddenCount: campaign.offers.length - shown.length,
              offers: shown.map((offer) => ({
                id: offer.id ?? "",
                name: offer.name ?? "",
                crossSegment: !(offer.conditions?.segments ?? []).includes(segmentId),
                segmentNames:
                  (offer.conditions?.segments ?? []).map((id) => segmentName(id ?? "")).join(", ") || "none",
              })),
            };
          }),
      };
    })
    .filter((group) => group.count > 0);
};

// Memoized per segment: the template reads this on every render, and the groups only change with the
// offers/campaigns/selection/expansion state.
const readViewGroupsBySegment = computed(() => {
  const map = new Map<string, ReturnType<typeof buildReadViewGroups>>();
  props.dynamicSegments.forEach((segment) => map.set(segment.id, buildReadViewGroups(segment.id)));
  return map;
});
const readViewGroups = (segmentId: string) => readViewGroupsBySegment.value.get(segmentId) ?? [];

// Add-only picker state. Only one picker is open at a time (one per visible segment tab); opening it
// resets the search and defaults the segment filter to the treatment's own segment.
const pickerOpenSegmentId = ref<string | null>(null);
const pickerSearch = ref("");
const pickerSegmentFilter = ref<string>("*");
const PICKER_CAP = 40;

// Precomputed per-offer picker metadata (lowercased search keys, campaign name, sort key). Recomputed only
// when the offers/campaigns/segments change — NOT per keystroke. `localeCompare` in the sort comparator was
// the picker's bottleneck at thousands of offers (O(n log n) locale-aware comparisons took seconds), so
// sorting now uses one precomputed plain-string key per offer.
const pickerOffersIndex = computed(() =>
  offers.value
    .filter((offer): offer is NonNullable<typeof offer> => !!offer)
    .map((offer) => {
      const name = offer.name ?? "";
      const campaignName = campaignNameOf(offer);
      const segments = (offer.conditions?.segments ?? []).filter((id): id is string => !!id);
      return {
        id: offer.id ?? "",
        name,
        isExclude: offer.abtestLinkMode === "EXCLUDE",
        campaignName,
        searchKey: `${name}\n${campaignName}`.toLowerCase(),
        sortKey: `${campaignName}\n${name}`.toLowerCase(),
        segments,
        segmentNames: segments.map(segmentName).join(", ") || "none",
      };
    })
);

// Memoized for the (single) open picker: the union of the offers' segments only changes with the offers
// list, so don't rebuild it on every render/keystroke.
const openPickerSegmentOptions = computed(() => {
  const segmentId = pickerOpenSegmentId.value;
  if (!segmentId) {
    return [];
  }
  const otherSegmentIds = new Set<string>();
  pickerOffersIndex.value.forEach((offer) => {
    offer.segments.forEach((id) => {
      if (id !== segmentId) {
        otherSegmentIds.add(id);
      }
    });
  });
  return [
    // The treatment's own segment — first, and the default filter.
    { label: segmentName(segmentId), value: segmentId },
    { label: "All segments", value: "*" },
    ...[...otherSegmentIds]
      .map((id) => ({ label: segmentName(id), value: id }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
});

const buildPickerData = (segmentId: string) => {
  const selected = new Set(getSelectedOffers(segmentId));
  const query = pickerSearch.value.trim().toLowerCase();
  const filter = pickerSegmentFilter.value;

  const matches = pickerOffersIndex.value.filter((offer) => {
    if (selected.has(offer.id)) {
      return false;
    }
    if (filter !== "*" && !offer.segments.includes(filter)) {
      return false;
    }
    return !query || offer.searchKey.includes(query);
  });

  const isOwnSegment = (offer: typeof matches[number]) => offer.segments.includes(segmentId);
  // Plain-string comparison on the precomputed sortKey: localeCompare here took seconds at thousands of
  // offers (O(n log n) locale-aware comparisons).
  matches.sort((a, b) => {
    const own = Number(isOwnSegment(b)) - Number(isOwnSegment(a));
    if (own) {
      return own;
    }
    const mode = Number(a.isExclude) - Number(b.isExclude);
    if (mode) {
      return mode;
    }
    if (a.sortKey === b.sortKey) {
      return 0;
    }
    return a.sortKey < b.sortKey ? -1 : 1;
  });

  const shown = matches.slice(0, PICKER_CAP);
  const groups: {
    label: string;
    mode: string;
    offers: { id: string; name: string; crossSegment: boolean; segmentNames: string }[];
  }[] = [];
  shown.forEach((offer) => {
    const mode = offer.isExclude ? "EXCLUDE" : "INCLUDE";
    const label = `${isOwnSegment(offer) ? "" : "Other segments — "}${mode === "EXCLUDE" ? "Exclude" : "Include"} · ${
      offer.campaignName
    }`;
    if (!groups.length || groups[groups.length - 1].label !== label) {
      groups.push({ label, mode, offers: [] });
    }
    groups[groups.length - 1].offers.push({
      id: offer.id,
      name: offer.name,
      crossSegment: !isOwnSegment(offer),
      segmentNames: offer.segmentNames,
    });
  });

  const countLine =
    matches.length > PICKER_CAP
      ? `Showing ${PICKER_CAP} of ${matches.length} available offers — refine the search to see more.`
      : `${matches.length} available offer${matches.length === 1 ? "" : "s"}.`;

  return { groups, totalCount: matches.length, countLine };
};

// Memoized picker data for the (single) open picker: the template reads it several times per render and
// buildPickerData filters+sorts the whole offers list, so a computed keeps that to once per change.
const openPickerData = computed(() =>
  pickerOpenSegmentId.value
    ? buildPickerData(pickerOpenSegmentId.value)
    : { groups: [] as ReturnType<typeof buildPickerData>["groups"], totalCount: 0, countLine: "" }
);

/**
 * Manage classes input
 */
const getClasses = (segmentId: string) => {
  const treatment = props.cohort.treatments.find((t) => t.segmentId === segmentId);
  const classes =
    treatment?.classesOverrides?.map((cls) => ({ name: cls.class.displayName, id: cls.class.technicalName })) ?? [];

  // VT.ShopManagementClass must be hidden in the UI
  return classes.filter((cls) => cls.id !== "VT.ShopManagementClass");
};

/**
 * Update Cohort
 */
const { mutate } = useCohortFormPageSaveMutation({
  refetchQueries: [queryNames.CohortsListPage],
});

const handleCohortUpdate = useDebounceFn(async (field: string, value: string) => {
  const input: CohortInput = {
    name: props.cohort.name,
    description: props.cohort.description,
    allocationRate: props.cohort.allocationRate,
    isDynamic: true,
    [field]: value,
  };

  if (props.cohort.treatments?.length > 0) {
    input.treatments = mapCohortUpdatePayload(props.cohort.treatments as unknown as CohortTreatment[]) ?? [];
  }

  await mutate({
    gameId: props.gameId,
    versionId: props.versionId,
    abtestId: props.abtestId,
    cohortId: props.cohort.id,
    input,
    useMultiLink: flags.value.offerMultiLinkABTest.enabled,
  });
}, 1000);

const handleShopChange = async (event: SelectChangeEvent, segmentId: string) => {
  const shouldClearShop = event.value === null;

  const input: CohortInput = {
    name: props.cohort.name,
    description: props.cohort.description,
    allocationRate: props.cohort.allocationRate,
    isDynamic: true,
    treatments: [],
  };

  if (props.cohort.treatments?.length > 0) {
    input.treatments = mapCohortUpdatePayload(cloneDeep(props.cohort.treatments) as unknown as CohortTreatment[]) ?? [];
  }

  const currentTreatment = input.treatments?.find((t) => t.segmentId === segmentId);

  // If a treatment exists, update shopId and VT class override with existing overrides
  if (currentTreatment) {
    const filteredOverrides =
      currentTreatment.classesOverrides?.filter((cls) => cls.classId !== "VT.ShopManagementClass") ?? [];

    currentTreatment.shopId = event.value?.id ?? null;

    if (!currentTreatment.treatmentTypes.includes(AbtestType.Shop) && !shouldClearShop) {
      currentTreatment.treatmentTypes.push(AbtestType.Shop);
    }
    if (shouldClearShop && currentTreatment.treatmentTypes.includes(AbtestType.Shop)) {
      currentTreatment.treatmentTypes = currentTreatment.treatmentTypes.filter((type) => type !== AbtestType.Shop);
    }

    currentTreatment.classesOverrides = [
      ...filteredOverrides,
      ...(shouldClearShop ? [] : [getVTShopManagementClass(event.value.id, shopManagementClassId.value)]),
    ];
  } else {
    input.treatments?.push({
      segmentId,
      shopId: event.value.id,
      rank: input.treatments ? input.treatments.length + 1 : 1,
      treatmentTypes: [AbtestType.Shop],
      id: "",
      classesOverrides: [getVTShopManagementClass(event.value.id, shopManagementClassId.value)],
    });
  }
  await mutate({
    gameId: props.gameId,
    versionId: props.versionId,
    abtestId: props.abtestId,
    cohortId: props.cohort.id,
    input,
    useMultiLink: flags.value.offerMultiLinkABTest.enabled,
  });
};

// While the dropdown is open, only record the selection locally (instant UI). The save happens on close.
const handleOffersChange = (event: MultiSelectChangeEvent, segmentId: string) => {
  pendingOffersBySegment.value = {
    ...pendingOffersBySegment.value,
    [segmentId]: (event.value ?? []) as string[],
  };
};

// Called when the offers editor saves (legacy: dropdown close): persist the segment's selection with a
// single saveCohort, but only if it actually changed.
const performOffersSave = async (segmentId: string) => {
  const pending = pendingOffersBySegment.value[segmentId];
  if (pending === undefined) {
    return; // editor opened/closed without any change
  }

  const serverOffers = props.cohort.treatments.find((t) => t.segmentId === segmentId)?.offers ?? [];
  if (sameOfferIds(pending, serverOffers)) {
    clearPendingOffers(segmentId); // no effective change
    return;
  }

  const input: CohortInput = {
    name: props.cohort.name,
    description: props.cohort.description,
    allocationRate: props.cohort.allocationRate,
    isDynamic: true,
    treatments: [],
  };

  if (props.cohort.treatments?.length > 0) {
    input.treatments = mapCohortUpdatePayload(props.cohort.treatments as unknown as CohortTreatment[]) ?? [];
  }

  const currentTreatment = input.treatments?.find((t) => t.segmentId === segmentId);

  if (currentTreatment) {
    if (pending.length === 0) {
      // Remove the offer treatment type when the last offer is unselected.
      currentTreatment.treatmentTypes = currentTreatment.treatmentTypes.filter((type) => type !== AbtestType.Offer);
      delete currentTreatment.offers;
    } else {
      currentTreatment.offers = pending;
      if (!currentTreatment.treatmentTypes.includes(AbtestType.Offer)) {
        currentTreatment.treatmentTypes = [...currentTreatment.treatmentTypes, AbtestType.Offer];
      }
    }
  } else if (pending.length > 0) {
    input.treatments?.push({
      segmentId,
      id: "",
      offers: pending,
      rank: input.treatments ? input.treatments.length + 1 : 1,
      treatmentTypes: [AbtestType.Offer],
    });
  }

  setSavingOffers(segmentId, true);
  try {
    const result = await mutate({
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId: props.abtestId,
      cohortId: props.cohort.id,
      input,
      useMultiLink: flags.value.offerMultiLinkABTest.enabled,
    });

    if (result?.data?.saveCohort.success !== true) {
      // Keep the optimistic selection so the change isn't silently lost; closing again retries the save.
      toastError({
        summary: "Couldn't save offers",
        detail: result?.data?.saveCohort?.errorMessage ?? "Please try again.",
        life: 4 * 1000,
      });
      return;
    }

    await refetchOffers();
    clearPendingOffers(segmentId); // props are now authoritative again
    toastSuccess({ summary: "Offers saved", detail: "The cohort treatment was updated.", life: 2 * 1000 });
  } catch (error) {
    toastError({ summary: "Couldn't save offers", detail: "Please try again.", life: 4 * 1000 });
  } finally {
    setSavingOffers(segmentId, false);
  }
};

// Serialize this component's cohort saves: updateCohort delete+recreates the cohort's treatments, so two
// overlapping PUTs race in the backend (one can fail on the treatments' unique constraint while the other
// lands) — the UI then reports a failure for a change that actually persisted.
let cohortSaveChain: Promise<void> = Promise.resolve();

const saveOffersForSegment = (segmentId: string) => {
  cohortSaveChain = cohortSaveChain.then(() => performOffersSave(segmentId));
  return cohortSaveChain;
};

// "Cancel": close edit mode and DISCARD everything picked/removed while it was open (the optimistic
// pending selection is dropped, so the read view falls back to the server state).
const cancelPicker = (segmentId: string) => {
  pickerOpenSegmentId.value = null;
  clearPendingOffers(segmentId);
};

const togglePicker = (segmentId: string) => {
  if (pickerOpenSegmentId.value === segmentId) {
    // "Save": persist everything picked while the picker was open in ONE request (no-op if unchanged).
    pickerOpenSegmentId.value = null;
    saveOffersForSegment(segmentId);
    return;
  }
  // Opening another segment's picker while one is open: save the open one first so picks aren't lost.
  if (pickerOpenSegmentId.value) {
    saveOffersForSegment(pickerOpenSegmentId.value);
  }
  pickerOpenSegmentId.value = segmentId;
  pickerSearch.value = "";
  pickerSegmentFilter.value = segmentId;
};

// Multi-link picker actions. Picks only update the optimistic pending selection — the single saveCohort
// request happens when the picker's "Save" button is clicked (togglePicker), mirroring the legacy
// save-on-dropdown-close batching. Guarded while a save is in flight so clicks can't race it.
const addOffer = (segmentId: string, offerId: string) => {
  if (savingOffersBySegment.value[segmentId]) {
    return;
  }
  const next = [...getSelectedOffers(segmentId), offerId];
  pendingOffersBySegment.value = { ...pendingOffersBySegment.value, [segmentId]: next };
};

// Removing a pill from the read view persists immediately — unless this segment's picker is open, in which
// case the removal joins the batch saved on "Save".
const removeOffer = (segmentId: string, offerId: string) => {
  if (savingOffersBySegment.value[segmentId]) {
    return;
  }
  const next = getSelectedOffers(segmentId).filter((id: string) => id !== offerId);
  pendingOffersBySegment.value = { ...pendingOffersBySegment.value, [segmentId]: next };
  if (pickerOpenSegmentId.value !== segmentId) {
    saveOffersForSegment(segmentId);
  }
};

const cohortName = computed({
  get: () => props.cohort.name,
  set: (value) => {
    handleCohortUpdate("name", value);
  },
});

const cohortDescription = computed({
  get: () => props.cohort.description,
  set: (value) => {
    handleCohortUpdate("description", value);
  },
});

const onClassClick = (segmentId: string, classId?: string) => {
  router.push({
    name: links.games.abtests.addClasses,
    query: {
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId: props.abtestId,
      cohortId: props.cohort.id,
      segmentId,
      classId,
    },
  });
};

const onClassCopy = (segmentId: string) => {
  router.push({
    name: links.games.abtests.copyClasses,
    query: {
      gameId: props.gameId,
      versionId: props.versionId,
      abtestId: props.abtestId,
      cohortId: props.cohort.id,
      segmentId,
    },
  });
};

// Redirect to the correct tab after assigning a class to a cohort
watch(
  () => props.routingCohortId,
  () => {
    if (props.routingCohortId && props.routingSegmentId) {
      activeTab.value = props.dynamicSegments.findIndex((s) => s.id === props.routingSegmentId);
    }
  },
  { immediate: true }
);
</script>

<style module lang="scss">
// PrimeVue renders the MultiSelect toggle-all as a bare checkbox with only an aria-label; add a visible
// "Select all" label next to it. The header is a flex row, so the ::after sits inline after the checkbox.
.selectAllHeader::after {
  content: "Select all";
  align-self: center;
  margin-left: 0.5rem;
  color: var(--vd-color-text-default);
  font-size: 14px;
}

.offersRow {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.multiselectOption {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .warningIcon {
    margin-left: 0.5rem;
  }
}

// Cross-segment marker (read-view pills, picker options, legacy MultiSelect unselect warning).
.warningIcon {
  color: var(--vd-color-yellow-500);
}

.offersEditor {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 320px;
  max-width: 560px;
}

.noOffers {
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 13px;
  font-style: italic;
}

.readGroup {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid var(--vd-color-neutral-light-8, #e5e7eb);
  border-radius: 8px;
  padding: 8px 10px;
}

// Pinned to the top of the scrolling read view while its group is in view, so the Include/Exclude context
// stays visible. Negative margins + matching padding make the opaque background span the group's full
// inner width, so pills don't peek around it while scrolling underneath.
.readGroupHeader {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  background: var(--vd-color-white, #fff);
  margin: -8px -10px 0;
  padding: 8px 10px;
}

.groupCount {
  color: var(--vd-color-text-light-2, #6b7280);
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}

// Read view on the left, add picker floating on the right while edit mode is open.
.editorBody {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

// Capped with internal scrolling so a bulk-linked treatment's long offer list doesn't stretch the page.
.readColumn {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 0;
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.campaignBlock {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

// Campaign header line above its offers.
.campaignName {
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 12px;
  font-weight: 600;
}

// One chip per line, its remove button beside it (outside the chip).
.pillList {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-left: 8px;
}

.pillRow {
  display: flex;
  align-items: center;
  gap: 6px;
}

.offerPill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 10px;
  border: 1px solid var(--vd-color-neutral-light-8, #e5e7eb);
  border-radius: 999px;
  background: var(--vd-color-neutral-light-10, #f9fafb);
  font-size: 12px;
}

.pillRemove {
  display: inline-flex;
  cursor: pointer;
  // VdIcon sets an inline `color: var(--vd-icon-color)` on the svg, so plain `color` on this wrapper
  // can't restyle it — override the variable instead.
  --vd-icon-color: var(--vd-color-text-light-2, #6b7280);

  &:hover {
    --vd-icon-color: var(--vd-color-red-default, #dc2626);
  }
}

// Space stays reserved (visibility, not display) so pills don't resize when edit mode toggles; a hidden
// element also receives no clicks.
.pillRemoveHidden {
  visibility: hidden;
}

.pillMoreLink {
  align-self: flex-start;
  color: var(--vd-color-blue-default, #3b82f6);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.pickerActions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cancelEditBtn {
  align-self: flex-start;
  padding: 7px 14px;
  border: 1px solid var(--vd-color-neutral-light-6, #d1d5db);
  border-radius: 8px;
  background: transparent;
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.addOffersBtn {
  align-self: flex-start;
  padding: 7px 14px;
  border: 1px dashed var(--vd-color-blue-default, #3b82f6);
  border-radius: 8px;
  background: transparent;
  color: var(--vd-color-blue-default, #3b82f6);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

// Add panel below the read view while edit mode is open, capped like the read view (.readColumn) with its
// offer list scrolling internally.
.picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid var(--vd-color-neutral-light-6, #d1d5db);
  border-radius: 8px;
  padding: 10px;
  max-height: 420px;
}

.pickerTitle {
  font-size: 14px;
  font-weight: 600;
}

.pickerControls {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  input {
    height: 32px;
  }
}

.pickerSearch {
  flex: 2;
  min-width: 160px;
}

.pickerFilter {
  flex: 1;
  min-width: 180px;
  height: 32px;
}

// Grows with the sticky panel (bounded by the panel's viewport-based max-height) and scrolls internally.
.pickerList {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.pickerGroupHeader {
  padding: 6px 6px 2px;
}

.pickerOption {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: var(--vd-color-neutral-light-10, #f3f4f6);
  }
}

.pickerDisabled {
  opacity: 0.6;
  pointer-events: none;
}

.pickerOptionName {
  flex: 1;
}

.pickerEmpty {
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 13px;
  font-style: italic;
  padding: 12px;
  text-align: center;
}

.pickerCount {
  color: var(--vd-color-text-light-2, #6b7280);
  font-size: 12px;
}

.groupHeader {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.modeInclude {
  color: #15803d;
}

.modeExclude {
  color: #b91c1c;
}

.formWrapper {
  display: flex;
  color: var(--vd-color-text-default);
  // Anchor for the picker rail overlaying the classes column.
  position: relative;

  .formField {
    margin-right: 2rem;

    label {
      display: block;
      margin-bottom: 0.5rem;
    }
  }

  .classesField {
    width: 240px;

    label {
      display: block;
      margin-bottom: 1rem;
    }

    p {
      font-size: 12px;
      color: var(--vd-color-text-light-2);
    }

    .classesAddButton {
      width: 100%;
      display: flex;
      justify-content: flex-end;
    }
  }

  .classItem {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 0.5rem;

    .className {
      color: var(--vd-color-blue-default);
    }

    .classIcon {
      margin-right: 0.5rem;
    }

    .editIcon {
      color: var(--vd-color-blue-default);
      cursor: pointer;
    }
  }
}

.textInput {
  border: 1px solid var(--vd-color-neutral-light-2);
  font-size: 12px;
  color: var(--vd-color-text-dark);
  border-radius: 0.5rem;
  height: 2.5rem;
  margin-right: 0.5rem;
  margin-bottom: 0.25rem;
}
</style>

<style scoped lang="scss">
.locked {
  color: $red-alert;
}
.cohort-item-wrapper {
  margin-bottom: 1rem;

  h2 {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;

    svg {
      margin-left: 0.5rem;
      cursor: pointer;
      color: var(--vd-color-text-light-2);
    }
  }

  button {
    margin-top: 0.25rem;
  }

  .classes-button-group {
    display: flex;

    :deep(.vd-button:first-child:not(:only-child)) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right: none;
    }

    :deep(.vd-button:last-child:not(:only-child)) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      margin-left: 0;
    }
  }

  :deep(.p-tabs .p-tablist-tab-list) {
    background-color: white;
    border-bottom: none;
  }

  :deep(.p-tabs .p-tablist-tab-list button) {
    border: 0;
    background-color: white;
  }

  :deep(.p-tabs .p-tablist-tab-list button.p-tab-active) {
    border: 0;
    background-color: white;
    border-bottom: 6px solid var(--vd-color-blue-default);
  }

  :deep(.p-select-label svg) {
    margin-right: 0.5rem;
    color: var(--vd-color-blue-default);
  }

  :deep(.p-select-label),
  :deep(.p-multiselect-label.p-placeholder),
  :deep(.p-multiselect .p-multiselect-label) {
    padding: 6px 10px;
    font-size: 12px;
  }
}
</style>
